import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import {
	HeadingsEnum,
	StoreAvailabilityDataSourceEnum,
	ZipAvailabilityDataSourceEnum,
	ZipValidationDataSourceEnum
} from '@inter-ikea-kompis/enums';
import type { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import type {
	IBundleShoppingProduct,
	IDexfSettings,
	ILocalizedInformation,
	IProduct,
	IShoppingProduct,
	IStore,
	IStoreAvailability,
	ITheme
} from '@inter-ikea-kompis/types';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';
import type { IKompisElementPropertyValue } from '@inter-ikea-kompis/web-component';

import { HomeDelivery } from '@inter-ikea-kompis/component-home-delivery';
import type { IProductListSection } from '@inter-ikea-kompis/component-product-list';
import { ProductList } from '@inter-ikea-kompis/component-product-list';
import type { ProductRowLayoutEnum } from '@inter-ikea-kompis/component-product-row';
import { ProductRowProductLinkEnum } from '@inter-ikea-kompis/component-product-row';
import { ProductRowProductMeasureEnum } from '@inter-ikea-kompis/component-product-row';
import type { SortListOptionsEnum } from '@inter-ikea-kompis/component-sort-list';
import { SortList } from '@inter-ikea-kompis/component-sort-list';
import { StoreSelectorLayoutEnum } from '@inter-ikea-kompis/component-store-selector';
import { StoreSelector } from '@inter-ikea-kompis/component-store-selector';
import { ZipInModalEnum } from '@inter-ikea-kompis/component-zip-in';

import SummaryPageModalEnum from '../../enums/SummaryPageModalEnum.js';
import type ISummaryPageHomeDeliveryProps from '../../types/ISummaryPageHomeDeliveryProps.js';
import type ISummaryPageTranslations from '../../types/ISummaryPageTranslations.js';
import type ISummaryPageVisibleModal from '../../types/ISummaryPageVisibleModal.js';
import SummaryPageProductListStyle from './SummaryPageProductListStyle.js';

/**
 * The view for the Summary Page Product List.
 *
 * @event storeSelectorChange Triggered when the selected store changes.
 * @event sortListChange Triggered when the selected way of sorting changes.
 * @event collectProduct Triggered when a product is collected.
 * @event modalOpen Triggered when a modal should be opened.
 * @event modalClose Triggered when a modal should be closed.
 * @event homeDeliveryClick User clicks on the home delivery link to select zip code.
 * @event productListRemove Triggered when the remove button is clicked on a product in product list.
 * @event productListExpand Triggered when included shopping products in product list gets expanded.
 * @event productListNameClick Triggered when the name of a product is clicked.
 */

export default class SummaryPageProductList extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: ISummaryPageTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IDexfSettings | null = null;

	// A list of shopping products or bundle shopping products.
	@property({ type: Object, required: true })
	public shoppingProducts!: (IShoppingProduct | IBundleShoppingProduct)[];

	// A list of store availability objects.
	@property({ type: Object })
	public storeAvailabilities: IStoreAvailability[] | null = null;

	// A list of stores, when left empty the store selector will be hidden
	@property({ type: Object })
	public stores: IStore[] | null = null;

	// The storeId of the selected store
	@property({ type: String })
	public storeId: string | null = null;

	// Controls how measurements should be shown
	@property({ type: String })
	public productMeasures: ProductRowProductMeasureEnum = ProductRowProductMeasureEnum.none;

	// Disables outgoing links.
	@property({ type: Boolean })
	public disableLinks = false;

	// Visible modal
	@property({ type: Object, synchronous: true })
	public visibleModal: ISummaryPageVisibleModal | null = null;

	// A collection of props used for the home delivery availability in product list.
	@property({ type: Object, synchronous: true })
	public homeDelivery: ISummaryPageHomeDeliveryProps | null = null;

	// Zip code will be passed down to home delivery.
	@property({ type: String })
	public zipCode: string | null = null;

	// Whether to show the collect checkbox in the product list or not.
	@property({ type: Boolean })
	public collectableProducts = false;

	// A list of product ids that are collected.
	@property({ type: Object })
	public collectedProducts: string[] | null = null;

	// Show text describing the color and/or material of the product.
	@property({ type: Boolean })
	public showDesignText = false;

	// A list of sections used in the product list.
	@property({ type: Object })
	public productListSections: IProductListSection[] | null = null;

	// A list of included products within SPR products. Must be set (if applicable) for total package weight.
	@property({ type: Object })
	public includedProducts: IProduct[] | null = null;

	// A list of product item numbers that can be added in the product list.
	@property({ type: Array })
	public productListAddable: string[] | null = null;

	// A list of product item numbers that can be removed in the product list.
	@property({ type: Object })
	public productListRemovable: string[] | null = null;

	// Sets the default sorting of the product list.
	@property({ type: String })
	public defaultSortBy: SortListOptionsEnum | null = null;

	// Highest heading on the Summary page will be h2 as opposed to h1. Set to true if planner is e.g. used in an iframe with an h1 header outside of the planner.
	@property({ type: Boolean })
	public startHeadingsAtH2 = false;

	// Layout for summary page. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print. Make sure SPR's has their child articles in includedProducts, since SPR's are not visible in compactPrint.
	@property({ type: String })
	public pickingListPrintLayout: ProductRowLayoutEnum | null = null;

	// Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
	@property({ type: String })
	public measurementSystem: MeasurementSystemEnum | null = null;

	// Recommended pattern for normal and kiosk mode, is eventOnly to open a ProductInformationCard in a Skapa sheet modal. If using productInformationPage, disable on kiosk.
	@property({ type: String })
	public productListLink: ProductRowProductLinkEnum =
		ProductRowProductLinkEnum.productInformationPage;

	// How the product list should be sorted
	private sortBy: SortListOptionsEnum | null = null;
	// A list of expanded shopping products.
	private expanded: string[] | null = null;

	private dexfSettings: IDexfSettings | null = null;

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		const homeDeliveryComponent = this.getHomeDelivery();
		const storeSelectorComponent = this.getStoreSelector();

		const showAvailabilityOptions = !!homeDeliveryComponent || !!storeSelectorComponent;

		return html`
			<style>
				${SummaryPageProductListStyle.getStyle({ theme: this.theme, showAvailabilityOptions })}
			</style>
			<div class="header">
				<div class="availability">${homeDeliveryComponent} ${storeSelectorComponent}</div>
				${this.getSortList()}
			</div>
			${this.getProductList()}
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-sort-list', SortList);
		this.customElements.define('kompis-product-list', ProductList);
		this.customElements.define('kompis-store-selector', StoreSelector);
		this.customElements.define('kompis-home-delivery', HomeDelivery);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, IKompisElementPropertyValue>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getTranslations(): ISummaryPageTranslations {
		return this.translations
			? this.translations
			: <ISummaryPageTranslations>this.localizedInformation?.translations;
	}

	private getSettings(): IDexfSettings {
		return <IDexfSettings>this.dexfSettings;
	}
	/**
	 * Get Home Delivery. When homeDelivery prop is undefined or null, the home delivery is hidden.
	 *
	 * @returns The home delivery.
	 */
	private getHomeDelivery(): RenderResult {
		const { zipValidationDataSource, zipAvailabilityDataSource } =
			this.getSettings().kompis.serviceSettings;

		const dataSourceIsDisabled =
			zipValidationDataSource === ZipValidationDataSourceEnum.disabled ||
			zipAvailabilityDataSource === ZipAvailabilityDataSourceEnum.disabled;

		// We need to convert this to a ZipInModalEnum so that we're passing the
		// correct thing to the home delivery component.
		const visibleModal =
			this.visibleModal?.modal === SummaryPageModalEnum.zipIn ? ZipInModalEnum.zipIn : null;

		if (!this.homeDelivery || dataSourceIsDisabled) {
			return null;
		}

		return html` <kompis-home-delivery
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.zipAvailabilities="${this.homeDelivery.zipAvailabilities}"
			.zipCode="${this.zipCode}"
			.visibleModal="${visibleModal}"
			@homeDeliveryClick="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('homeDeliveryClick', { bubbles: true, detail: event.detail })
				);
			}}"
		></kompis-home-delivery>`;
	}

	/**
	 * Get Store Selector. When stores prop is undefined or empty list, store selector is hidden.
	 *
	 * @returns The store selector.
	 */
	private getStoreSelector(): RenderResult {
		if (
			!this.stores ||
			this.stores?.length === 0 ||
			// No reason to show the store selector if there are no store availabilties.
			this.getSettings().kompis.serviceSettings.storeAvailabilityDataSource ===
				StoreAvailabilityDataSourceEnum.disabled
		) {
			return null;
		}

		return html` <kompis-store-selector
			.stores="${this.stores}"
			.theme="${this.theme}"
			.layout="${StoreSelectorLayoutEnum.link}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.storeId="${this.storeId}"
			.storeAvailabilities="${this.storeAvailabilities}"
			.visibleModal="${this.visibleModal?.modal || null}"
			@modalOpen="${(): boolean =>
				this.dispatchEvent(
					new CustomEvent('modalOpen', {
						bubbles: true,
						detail: {
							visibleModal: <ISummaryPageVisibleModal>{
								itemId: null,
								modal: SummaryPageModalEnum.storeSelectorCard
							}
						}
					})
				)}"
			@modalClose="${() => {
				this.dispatchEvent(
					new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } })
				);
			}}"
			@change="${({ detail }) => {
				this.dispatchEvent(
					new CustomEvent('storeSelectorChange', {
						bubbles: true,
						detail: { storeId: detail.storeId, visibleModal: null }
					})
				);
			}}"
		></kompis-store-selector>`;
	}

	/**
	 * Get Sort List.
	 *
	 * @returns The sort list.
	 */
	private getSortList(): RenderResult {
		return html` <kompis-sort-list
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.selected="${this.sortBy ?? this.defaultSortBy}"
			.visibleModal="${this.visibleModal?.modal || null}"
			@modalOpen="${(): boolean =>
				this.dispatchEvent(
					new CustomEvent('modalOpen', {
						bubbles: true,
						detail: {
							visibleModal: <ISummaryPageVisibleModal>{
								itemId: null,
								modal: SummaryPageModalEnum.sortListCard
							}
						}
					})
				)}"
			@modalClose="${({ detail }) => {
				this.dispatchEvent(
					new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } })
				);
				detail.triggerElement?.focus();
			}}"
			@change="${({ detail }): void => {
				this.dispatchEvent(
					new CustomEvent('sortListChange', {
						bubbles: true,
						detail: { selected: detail.selected, visibleModal: detail.visibleModal }
					})
				);
				this.sortBy = detail.selected;
				detail.triggerElement?.focus();
				this.requestUpdate();
			}}"
		></kompis-sort-list>`;
	}

	/**
	 * Get Product List.
	 *
	 * @returns The product list.
	 */
	private getProductList(): RenderResult {
		return html`<kompis-product-list
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.shoppingProducts="${this.shoppingProducts}"
			.storeAvailabilities="${this.storeAvailabilities}"
			.stores="${this.stores}"
			.storeId="${this.storeId}"
			.includedProducts="${this.includedProducts}"
			.sortBy="${this.sortBy ?? this.defaultSortBy}"
			.productMeasures="${this.productMeasures}"
			.disableLinks="${this.disableLinks}"
			.visibleModal="${this.visibleModal?.itemId !== null ? this.visibleModal : null}"
			.showItemNo="${true}"
			.zipAvailabilities="${this.homeDelivery?.zipAvailabilities}"
			.collectable="${this.collectableProducts}"
			.collected="${this.collectedProducts}"
			.showDesignText="${this.showDesignText}"
			.skipListingLabel="${this.getTranslations().skipListingButtonLabel}"
			.sections="${this.productListSections}"
			.expanded="${this.expanded}"
			.addable="${this.productListAddable}"
			.removable="${this.productListRemovable}"
			.maxHeadingLevel="${this.startHeadingsAtH2 ? HeadingsEnum.h3 : HeadingsEnum.h2}"
			.layout="${this.pickingListPrintLayout}"
			.measurementSystem="${this.measurementSystem}"
			.productLink="${this.productListLink}"
			@productNameClick="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('productListNameClick', { bubbles: true, detail: { ...event.detail } })
				);
			}}"
			@expand="${(event: CustomEvent) => {
				this.expanded = event.detail.expanded;
				this.requestUpdate();
				this.dispatchEvent(
					new CustomEvent('productListExpand', { bubbles: true, detail: event.detail })
				);
			}}"
			@modalOpen="${(event: CustomEvent) =>
				this.dispatchEvent(new CustomEvent('modalOpen', { bubbles: true, detail: event.detail }))}"
			@modalClose="${(event: CustomEvent) =>
				this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
			@collect="${(event: CustomEvent) =>
				this.dispatchEvent(
					new CustomEvent('collectProduct', { bubbles: true, detail: event.detail })
				)}"
			@add="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('productListAdd', { bubbles: true, detail: event.detail })
				);
			}}"
			@remove="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('productListRemove', { bubbles: true, detail: event.detail })
				);
			}}"
		>
		</kompis-product-list>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-summary-page-product-list': SummaryPageProductList;
	}
}
